import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import PowerRankings from "@/components/PowerRankings";
import SeasonStats2025 from "@/components/SeasonStats2025";

export default function Stats() {
  const [team, setTeam] = useState(null);
  const [standings, setStandings] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [t, s] = await Promise.all([api.get("/team"), api.get("/standings")]);
        setTeam(t.data);
        setStandings(s.data);
      } catch (e) {
        toast.error("Stats failed to load");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const record = team?.record?.items?.[0];
  const stats = record?.stats || [];
  const recStat = (n) => stats.find((s) => s.name === n)?.value;
  const wins = recStat("wins") ?? 0;
  const losses = recStat("losses") ?? 0;
  const ties = recStat("ties") ?? 0;
  const pointsFor = recStat("pointsFor") ?? 0;
  const pointsAgainst = recStat("pointsAgainst") ?? 0;
  const diff = recStat("differential") ?? pointsFor - pointsAgainst;
  const games = wins + losses + ties || 1;
  const winPct = (recStat("winPercent") ?? wins / games) * 100;

  // Extract AFC South standings
  const afcSouth = (() => {
    if (!standings?.children) return [];
    for (const conf of standings.children) {
      for (const div of conf.children || []) {
        if (div.name?.toLowerCase().includes("south")) {
          return (div.standings?.entries || []).map((e) => ({
            name: e.team?.displayName,
            abbr: e.team?.abbreviation,
            logo: e.team?.logos?.[0]?.href,
            wins: e.stats?.find((s) => s.name === "wins")?.value ?? 0,
            losses: e.stats?.find((s) => s.name === "losses")?.value ?? 0,
            ties: e.stats?.find((s) => s.name === "ties")?.value ?? 0,
            pct: e.stats?.find((s) => s.name === "winPercent")?.value ?? 0,
          }));
        }
      }
    }
    return [];
  })();

  return (
    <div data-testid="stats-page" className="max-w-[1400px] mx-auto px-5 md:px-8 py-16">
      <div className="mb-10">
        <div className="font-cond uppercase tracking-[0.3em] text-xs text-titans-blue">By the Numbers</div>
        <h1 className="font-display text-5xl md:text-7xl uppercase tracking-wide section-title">Team Stats</h1>
      </div>

      {loading ? (
        <div className="text-titans-silver">Crunching the numbers…</div>
      ) : (
        <>
          <div className="grid md:grid-cols-4 gap-5 mb-10" data-testid="stats-big-numbers">
            {[
              { label: "Wins", value: wins, variant: "flag-card-blue", numCls: "text-white", labelCls: "text-white/90" },
              { label: "Losses", value: losses, variant: "flag-card-white", numCls: "text-titans-navy", labelCls: "text-titans-navy/70" },
              { label: "Points For", value: pointsFor, variant: "flag-card-red", numCls: "text-white", labelCls: "text-white/90" },
              { label: "Points Against", value: pointsAgainst, variant: "titans-card", numCls: "text-titans-blue", labelCls: "text-titans-silver" },
            ].map((m) => (
              <div key={m.label} className={`${m.variant} p-6`}>
                <div className={`font-cond uppercase tracking-[0.25em] text-[10px] ${m.labelCls}`}>{m.label}</div>
                <div className={`stat-number ${m.numCls} mt-2`}>{Number(m.value).toFixed(0)}</div>
              </div>
            ))}
          </div>

          <div className="grid md:grid-cols-3 gap-5 mb-12">
            <div className="titans-card p-6 md:col-span-2">
              <div className="font-cond uppercase tracking-[0.25em] text-xs text-titans-silver mb-3">Win Percentage</div>
              <div className="flex items-end gap-4">
                <div className="stat-number text-white">{(winPct).toFixed(1)}%</div>
                <div className="text-titans-silver text-sm mb-2">{wins}-{losses}{ties ? `-${ties}` : ""}</div>
              </div>
              <div className="mt-4 h-3 bg-titans-elev relative overflow-hidden">
                <div
                  className="absolute inset-y-0 left-0 bg-titans-red"
                  style={{ width: `${Math.min(100, winPct)}%` }}
                />
              </div>
            </div>
            <div className="titans-card p-6">
              <div className="font-cond uppercase tracking-[0.25em] text-xs text-titans-silver mb-3">Point Differential</div>
              <div className={`stat-number ${diff >= 0 ? "text-titans-red" : "text-titans-silver"}`}>
                {diff >= 0 ? "+" : ""}{Number(diff).toFixed(0)}
              </div>
            </div>
          </div>

          <div className="mb-6">
            <h2 className="font-display text-3xl md:text-4xl uppercase tracking-wide section-title">AFC South</h2>
          </div>
          <div className="titans-card overflow-hidden" data-testid="standings-table">
            <table className="w-full">
              <thead className="bg-titans-elev">
                <tr className="text-left font-cond uppercase tracking-wider text-xs text-titans-silver">
                  <th className="px-5 py-3">Team</th>
                  <th className="px-5 py-3">W</th>
                  <th className="px-5 py-3">L</th>
                  <th className="px-5 py-3">T</th>
                  <th className="px-5 py-3">PCT</th>
                </tr>
              </thead>
              <tbody>
                {afcSouth.map((t, i) => (
                  <tr key={t.abbr} className={`border-t border-[rgba(138,141,143,0.15)] ${t.abbr === "TEN" ? "bg-titans-elev/50" : ""}`}>
                    <td className="px-5 py-4 flex items-center gap-3">
                      {t.logo && <img src={t.logo} alt={t.abbr} className="h-7 w-7" />}
                      <span className="font-display text-lg uppercase">{t.name}</span>
                    </td>
                    <td className="px-5 py-4 font-display text-xl">{t.wins}</td>
                    <td className="px-5 py-4 font-display text-xl">{t.losses}</td>
                    <td className="px-5 py-4 font-display text-xl">{t.ties}</td>
                    <td className="px-5 py-4 font-cond text-titans-blue">{Number(t.pct).toFixed(3)}</td>
                  </tr>
                ))}
                {afcSouth.length === 0 && (
                  <tr><td colSpan={5} className="px-5 py-6 text-titans-silver">Standings unavailable.</td></tr>
                )}
              </tbody>
            </table>
          </div>

          <SeasonStats2025 />

          <div className="mt-12">
            <PowerRankings compact={true} />
          </div>
        </>
      )}
    </div>
  );
}
